import React, { useState, useEffect } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import deleteIcon from '../../../shared/assets/svg/cancel_icon.svg';
import editIcon from '../../../shared/assets/svg/edit.svg'; // Add this import
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const validationSchema = yup.object().shape({
  ocassion: yup.string().required('Occasion is required'),
  end_date: yup
    .string()
    .required('End date is required')
    .test('is-after-start', 'End date must be after start date', function (value) {
      const { start_date } = this.parent;
      return !start_date || !value || new Date(value) >= new Date(start_date);
    }),
  start_date: yup.string().required('Start date is required')
});

const AddEditHolidays = ({
  onClose,
  onSubmit,
  isSubmitting,
  modalType,
  selectedHoliday,
  setModalType,
  initialStartDate = null
}) => {
  const userId = Cookies.get('userId');
  const [startDate, setStartDate] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
    trigger,
    clearErrors
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange'
  });

  const [holidays, setHolidays] = useState([]);

  // Add this utility function to format the date
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // In your component, modify the useEffect for prefilling the form
  
  // Track page view
  useEffect(() => {
    mixpanel.track('Add Edit Holidays Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (modalType === 'Edit' && selectedHoliday) {
      setValue('ocassion', selectedHoliday.ocassion || '');
      setValue('start_date', formatDateForInput(selectedHoliday.start_date));
      setValue('end_date', formatDateForInput(selectedHoliday.end_date));
      setStartDate(formatDateForInput(selectedHoliday.start_date));
    } else if (modalType === 'Add' && initialStartDate) {
      setValue('start_date', initialStartDate);
      setStartDate(initialStartDate);
      setValue('end_date', '');
      setValue('ocassion', '');
      clearErrors();
      trigger(['start_date', 'end_date', 'ocassion']);
    }
  }, [modalType, selectedHoliday, initialStartDate, setValue, clearErrors, trigger]);

  // Also update the handleEditHoliday function
  const handleEditHoliday = async (index) => {
    const holidayToEdit = holidays[index];
    setValue('ocassion', holidayToEdit.ocassion);
    setValue('start_date', formatDateForInput(holidayToEdit.start_date));
    setValue('end_date', formatDateForInput(holidayToEdit.end_date));
    setStartDate(formatDateForInput(holidayToEdit.start_date));
    setEditingIndex(index);
    clearErrors();
    await trigger();
  };

  const handleAddHoliday = async (data) => {
    const isValid = await trigger();
    if (!isValid) {
      toast.error('Please fill in all the fields correctly');
      return;
    }

    const newHoliday = {
      ocassion: data.ocassion,
      start_date: data.start_date,
      end_date: data.end_date
    };

    if (editingIndex !== null) {
      // Update existing holiday
      setHolidays((prev) =>
        prev.map((holiday, index) => (index === editingIndex ? newHoliday : holiday))
      );
      setEditingIndex(null);
    } else {
      // Add new holiday
      setHolidays((prev) => [...prev, newHoliday]);
    }

    reset();
  };

  const handleDeleteHoliday = (index) => {
    setHolidays((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      reset();
    }
  };

  const handleFinalSubmit = async () => {
    const formData = getValues();
    const hasTyped =
      formData.ocassion?.trim() || formData.start_date?.trim() || formData.end_date?.trim();

    let finalHolidays = [...holidays];

    if (hasTyped) {
      const isValid = await trigger();
      if (!isValid) {
        toast.error('Please fill in all the fields correctly');
        return;
      }

      const newHoliday = {
        ocassion: formData.ocassion,
        start_date: formData.start_date,
        end_date: formData.end_date
      };

      if (editingIndex !== null) {
        finalHolidays[editingIndex] = newHoliday;
      } else {
        finalHolidays.push(newHoliday);
      }
    }

    if (finalHolidays.length === 0) {
      toast.error('Please add at least one holiday');
      return;
    }

    const payload = {
      holidays: finalHolidays.filter(
        (holiday) => holiday.ocassion && holiday.start_date && holiday.end_date
      )
    };

    onSubmit(payload);
    reset();
    setHolidays([]);
    setEditingIndex(null);
  };

  const onSubmitForm = (data) => {
    if (modalType === 'Edit') {
      onSubmit({ holidays: [data] });
    } else {
      handleAddHoliday(data);
    }
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Holiday Details</span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={32}
            label="Occasion"
            register={register}
            register_key="ocassion"
            type="text"
            isRequired
            errors={errors.ocassion}
          />
          <FormInput
            width={32}
            label="Start Date"
            register={register}
            register_key="start_date"
            type="date"
            isRequired
            errors={errors.start_date}
            onType={(text) => {
              setValue('end_date', '');
              setValue('start_date', text);
              setStartDate(text);
            }}
          />
          <FormInput
            width={32}
            label="End Date"
            register={register}
            register_key="end_date"
            type="date"
            isRequired
            CustomFeature={{
              min: startDate // Set minimum date to start date
            }}
            errors={errors.end_date}
          />
        </div>

        {/* Show only in Add mode */}
        {modalType === 'Add' && (
          <div className="w-full flex justify-end items-center space-x-4">
            <button
              className="text-customBlue border-b-2 border-[#2A565E]"
              type="submit"
              disabled={!isValid}>
              {editingIndex !== null ? 'Update Holiday' : 'Add Holiday'}
            </button>
          </div>
        )}
      </form>

      {/* Holiday List (Only for Add) */}
      {modalType === 'Add' && holidays.length > 0 && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-2">Added Holidays</h3>
          <ul className="space-y-2">
            {holidays.map((holiday, index) => (
              <li key={index} className="flex justify-between items-center border p-2 rounded">
                <span>
                  {holiday.ocassion || 'N/A'} - {holiday.start_date || 'N/A'} to{' '}
                  {holiday.end_date || 'N/A'}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => handleEditHoliday(index)}>
                    <img src={editIcon} alt="Edit" className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDeleteHoliday(index)}>
                    <img src={deleteIcon} alt="Delete" className="w-5 h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button
          type="button"
          disabled={
            isSubmitting || (modalType === 'Add' && holidays.length === 0 && !getValues().ocassion)
          }
          onClick={modalType === 'Add' ? handleFinalSubmit : handleSubmit(onSubmitForm)}>
          {modalType === 'Add' ? 'Submit' : 'Update'}
        </Button>
      </div>
    </>
  );
};

export default AddEditHolidays;
